import { DateTime } from "luxon";
import { type CreateCalendarEventDto } from "../../api/server.api";
import { assertDefined, assertValid } from "../../backoffice.utils";
import { type OtherEventFormValues } from "../../misc/calendar/OtherEventForm";
import { type OtherEventDuration } from "../../model/autovioCalendarEvents";
import { type OtherCalendarEventCategory } from "../../providers/drivingSchoolsProvider";

type CreateOtherEventDto = Extract<CreateCalendarEventDto, { type: "Other" }>;

export function createOtherEventDtos(props: {
  category: OtherCalendarEventCategory;
  createUid: () => string;
  formValues: OtherEventFormValues;
  instructorId: string;
}): CreateOtherEventDto[] {
  const { category, createUid, formValues, instructorId } = props;
  const { categoryId, start, endDate, halfDayStartTime, end, duration, notes, vehicleId } = formValues;
  if (!categoryId) {
    throw new Error(`Invalid categoryId: ${categoryId}`);
  }
  const startDate = assertValid(start, "start");
  const shouldSubmitDuration = category.availableDurations.length > 0;
  const isVacationEventWithHalfDayDuration = category.type === "vacation" && duration === "halfDay";
  if (isVacationEventWithHalfDayDuration && !halfDayStartTime) {
    throw new Error("halfDayStartTime is required for halfDay vacation events.");
  }
  const dates = shouldSubmitDuration
    ? getDatesInInclusiveRange(startDate, endDate ? assertValid(endDate, "endDate") : startDate)
    : [startDate];

  return dates.map((date) => ({
    type: "Other" as const,
    uid: createUid(),
    instructorId,
    categoryId,
    ...(vehicleId && { vehicleId }),
    ...(shouldSubmitDuration
      ? {
          otherEventDuration: durationToSubmit(category, duration, halfDayStartTime),
          start: date
            .set(
              isVacationEventWithHalfDayDuration
                ? { hour: halfDayStartTime!.hour, minute: halfDayStartTime!.minute }
                : { hour: duration === "halfDayInTheAfternoon" ? 12 : 8, minute: 0 },
            )
            .toISO(),
        }
      : {
          start: date.toISO(),
          end: assertValid(end, "end")
            .set({
              year: date.year,
              month: date.month,
              day: date.day,
            })
            .toISO(),
        }),
    notes: notes ?? "",
  }));
}

export function durationToSubmit(
  category: OtherCalendarEventCategory,
  initialDuration: OtherEventDuration | null,
  startTime?: DateTime | null,
): OtherEventDuration {
  const duration = assertDefined(initialDuration, "duration");
  if (category.type === "vacation" && duration === "halfDay" && startTime) {
    return startTime.hour >= 12 ? "halfDayInTheAfternoon" : "halfDayInTheMorning";
  }
  return duration;
}

function getDatesInInclusiveRange(start: DateTime, end: DateTime): DateTime[] {
  const firstDate = start.startOf("day");
  const lastDate = end.startOf("day");
  if (lastDate < firstDate) {
    throw new Error("Das Enddatum darf nicht vor dem Startdatum liegen.");
  }

  const dates: DateTime[] = [];
  let currentDate = firstDate;
  while (currentDate <= lastDate) {
    dates.push(currentDate);
    currentDate = currentDate.plus({ days: 1 });
  }
  return dates;
}
